import {
    Box,
    Button,
    Text,
    Switch,
    Input,
    InputGroup,
    InputLeftAddon,
    Checkbox,
    Modal,
    ModalOverlay,
    ModalContent,
    ModalHeader,
    ModalBody,
    ModalFooter,
    ModalCloseButton,
    Spinner,
    useDisclosure
} from '@chakra-ui/react';
import { CopyIcon, RepeatIcon,  CheckCircleIcon,   WarningIcon  } from '@chakra-ui/icons';
import React, { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import 'react-toastify/dist/ReactToastify.css';
import { ToasterNotification } from '@/toaster/toasterNotification';
import * as constants from '@/utils/constants';
import {
  callToGetCustomWebhookTestConnectionResponse,
  callToGetCustomWebhookRotateResponse,
  callToUpdateCustomWebhookConfiguration, 
  callToGetCustomWebhookConfigurationResponse,
  setCustomWebhookIsPostDataSave,
  setCustomWebhookIsLoadingRotate,
  setCustomWebhookIsLoadingSave,
  clearCustomWebhookTestResult,
  setCustomWebhookIsLoadingTestConnection,
} from '../ducks/slice';

// TODO: Import selectors from '../ducks/selectors'
import {
  getCustomWebhookData, getCustomWebhookIsLoadingRotate, getCustomWebhookIsLoadingSave, getCustomWebhookIsPostDataSave, getCustomWebhookIsLoadingTestConnection,
  getCustomWebhookTestResult,
  getCustomWebhookRotateResult
} from '../ducks/selectors';

export default function CustomWebhook() {
    const dispatch = useDispatch();
    const { isOpen, onOpen, onClose } = useDisclosure();  // ✅ Add this

    const webhookData = getCustomWebhookData();
    const isLoadingSave= getCustomWebhookIsLoadingSave();
    const onPostDataSave = getCustomWebhookIsPostDataSave();
    const isLoadingRotate = getCustomWebhookIsLoadingRotate()
    const isTestConnectionLoading = getCustomWebhookIsLoadingTestConnection()
    const getTestingResult = getCustomWebhookTestResult()
    const getRotateResult = getCustomWebhookRotateResult()
    
    const [saveError, setSaveError] = useState('');
    const [isEnabled, setIsEnabled] = useState(false);
    const [webhookUrl, setWebhookUrl] = useState('');
    const [signature, setSignature] = useState('');
    const [isActionDisabled, setIsActionDisabled] = useState(true);
    const [sendLinkedStandalone, setSendLinkedStandalone] = useState(false);
    const [sendLinkedEcommerce, setSendLinkedEcommerce] = useState(false);
    const [linkedDelayMinutes, setLinkedDelayMinutes] = useState<number>(0);
    const [delayDisplayValue, setDelayDisplayValue] = useState<string>('0');

    const [isCopied, setIsCopied] = useState(false);
    const [initialData, setInitialData] = useState({
        isEnabled: false,
        webhookUrl: '',
        signature: '',
        sendLinkedStandalone: false,
        sendLinkedEcommerce: false,
        linkedDelayMinutes: 0,
    });

    // Page load pe GET API call
    useEffect(() => {
        setSaveError('');
        fetchWebhookData();
    }, []);

    useEffect(() => {
        if (webhookData) {
            console.log({webhookData})
            setIsEnabled(webhookData.is_enabled);
            setWebhookUrl(webhookData.url || '');
            setSignature(webhookData.secret || '');
            setSendLinkedStandalone(webhookData.send_webhook_for_linked_transfers_standalone ?? false);
            setSendLinkedEcommerce(webhookData.send_webhook_for_linked_transfers_ecommerce ?? false);
            const delay = webhookData.linked_transfer_webhook_delay_minutes ?? 0;
            setLinkedDelayMinutes(delay);
            setDelayDisplayValue(String(delay));

            const initialState = {
                isEnabled: webhookData.is_enabled,
                webhookUrl: webhookData.url || '',
                signature: webhookData.secret || '',
                sendLinkedStandalone: webhookData.send_webhook_for_linked_transfers_standalone ?? false,
                sendLinkedEcommerce: webhookData.send_webhook_for_linked_transfers_ecommerce ?? false,
                linkedDelayMinutes: webhookData.linked_transfer_webhook_delay_minutes ?? 0,
            };
            setInitialData(initialState);
            if(isLoadingRotate){
                setCustomWebhookIsLoadingRotate(false)
            }
        }
    }, [webhookData]);

    const fetchWebhookData = async () => {
        try {
            dispatch(callToGetCustomWebhookConfigurationResponse());
        } catch (error) {
            console.error('Error fetching webhook data:', error);
        }
    };

    useEffect(()=> {
        if(onPostDataSave){
            ToasterNotification('success', 'Data saved successfully!');
            dispatch(setCustomWebhookIsPostDataSave(false));
        }
    }, [onPostDataSave])

    useEffect(()=>{
        if(getRotateResult?.success){
            ToasterNotification('success', 'Secret Updated successfully!');
            dispatch(setCustomWebhookIsLoadingRotate(false))
        }
    }, [getRotateResult])

    const handleToggle = async () => {
        try {
            const newEnabledState = !isEnabled;
            setIsEnabled(newEnabledState);
            if (!newEnabledState) {
                // Immediately persist the disabled state — no URL/secret required
                dispatch(callToUpdateCustomWebhookConfiguration({ is_enabled: false }));
            } else {
                checkIfChanged(webhookUrl, signature, newEnabledState);
            }
        } catch (error) {
            console.error('Error toggling webhook:', error);
        }
    };

    const handleTestConnection = async () => {
        try {
            if(onSaveValidation()){
                onOpen(); 
                dispatch(callToGetCustomWebhookTestConnectionResponse({webhookUrl}));
            }
        } catch (error) {
            console.error('Error testing connection:', error);
        }
    };

    const handleCopySignature = () => {
        if (signature) {
            navigator.clipboard.writeText(signature);
            setIsCopied(true);
            setTimeout(() => setIsCopied(false), 800);
        }
    };

    const handleRotateSignature = async () => {
        try {
            dispatch(callToGetCustomWebhookRotateResponse({webhookUrl}))
        } catch (error) {
            console.error('Error rotating signature:', error);
        } finally {
            // setIsLoading(false); // Remove
        }
    };

    const handleUrlChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        let value = e.target.value;
        // ✅ Remove protocol if user pastes full URL
        // value = value.replace(/^(https?:\/\/)/, '');

        setWebhookUrl(value);
        setSaveError('');
        checkIfChanged(e.target.value, signature, isEnabled);
    };

    const checkIfChanged = (
        url: string,
        sig: string,
        enabled: boolean,
        standalone = sendLinkedStandalone,
        ecommerce = sendLinkedEcommerce,
        delay = linkedDelayMinutes,
    ) => {
        const hasChanges =
            url !== initialData.webhookUrl ||
            sig !== initialData.signature ||
            enabled !== initialData.isEnabled ||
            standalone !== initialData.sendLinkedStandalone ||
            ecommerce !== initialData.sendLinkedEcommerce ||
            delay !== initialData.linkedDelayMinutes;
        setIsActionDisabled(!hasChanges);
    };
    const onSaveValidation = () => {
        if (isEnabled) {
            if (!webhookUrl || webhookUrl.trim() === '') {
                setSaveError('Please enter a webhook URL');
                return false;
            }
            if (webhookUrl.toLowerCase().includes('http://') ||
                webhookUrl.toLowerCase().includes('https://')) {
                setSaveError('Please remove http:// or https:// from URL');
                return false;
            }
            if (!webhookUrl.includes('.') || webhookUrl.startsWith('.') || webhookUrl.endsWith('.')) {
                setSaveError('Please enter a valid domain');
                return false;
            }
        }
        setSaveError('');
        return true;
    }

    const handleSave = async () => {
        try {
            console.log({webhookUrl, signature, isEnabled})
            if(onSaveValidation()){
                dispatch(callToUpdateCustomWebhookConfiguration({
                    url: webhookUrl,
                    secret: signature,
                    is_enabled: isEnabled,
                    send_webhook_for_linked_transfers_standalone: sendLinkedStandalone,
                    send_webhook_for_linked_transfers_ecommerce: sendLinkedEcommerce,
                    linked_transfer_webhook_delay_minutes: linkedDelayMinutes,
                }))
                console.log('Saved webhook config');
            }
        } catch (error) {
            console.error('Error saving webhook config:', error);
        }
    };

    const handleCancel = () => {
        setIsEnabled(initialData.isEnabled);
        setWebhookUrl(initialData.webhookUrl);
        setSignature(initialData.signature);
        setSendLinkedStandalone(initialData.sendLinkedStandalone);
        setSendLinkedEcommerce(initialData.sendLinkedEcommerce);
        setLinkedDelayMinutes(initialData.linkedDelayMinutes);
        setDelayDisplayValue(String(initialData.linkedDelayMinutes));
        setIsActionDisabled(true);
    };

    const maskSecret = (secret: string) => {
        if (!secret || secret.length < 4) return secret;
        const lastFour = secret.slice(-4);
        return '*'.repeat(20) + lastFour;
    };

    const handleModalClose = () => {
        console.log("me close me hu")
        dispatch(clearCustomWebhookTestResult());
        dispatch(setCustomWebhookIsLoadingTestConnection(false))
        onClose();
    };

    return (
        <>
        <Box className='filters-container'>
            <Box className='filter-box-container'>
                {/* Header */}
                <Box className='gray-header'>
                    <Text className='black_label' sx={{ width: '50%' }}>Webhook</Text>
                    <Box sx={{ width: '50%', display: 'flex', justifyContent: 'flex-end', alignItems: 'center' }}>
                        <Switch
                            size="md"
                            colorScheme="facebook"
                            isChecked={isEnabled}
                            onChange={handleToggle}
                        />
                    </Box>
                </Box>

                {isEnabled && (
                    <>
                        {/* URL Field - All in one line */}
                        <Box className='item-row' sx={{
                            display: 'flex !important',
                            alignItems: 'center',
                            gap: '20px',
                            padding: '20px !important'
                        }}>
                            <Text className='filter_text' sx={{
                                minWidth: '100px',
                                marginBottom: '0 !important',
                                fontWeight: '500'
                            }}>
                                URL:
                            </Text>
                            <InputGroup flex={1} maxWidth="500px">
                                <InputLeftAddon children="https://" />
                                <Input
                                    placeholder="example.com/webhook"
                                    value={webhookUrl}
                                    onChange={handleUrlChange}
                                    // TODO: Redux loading use karo
                                    isDisabled={isLoadingSave}
                                />
                            </InputGroup>
                            <Button
                                colorScheme="blue"
                                background="#ffffff"
                                color="#0C1E46"
                                border="1px solid #0C1E46"
                                padding="8px 20px"
                                size="sm"
                                onClick={handleTestConnection}
                                // TODO: Redux testing state use karo
                                isLoading={isTestConnectionLoading}
                                isDisabled={!webhookUrl || !signature || isTestConnectionLoading}
                            >
                                Test Connection
                            </Button>
                        </Box>

                        {/* Signature Field - All in one line */}
                        <Box className='item-row' sx={{
                            display: 'flex !important',
                            alignItems: 'center',
                            gap: '20px',
                            padding: '20px !important'
                        }}>
                            <Text className='filter_text' sx={{
                                minWidth: '100px',
                                marginBottom: '0 !important',
                                fontWeight: '500'
                            }}>
                                Secret:
                            </Text>
                           <Text
                                flex={1}
                                maxWidth="500px"
                                fontSize="sm"
                                bg={'white'}
                                padding="8px"
                                borderRadius="md"
                                // border="1px solid #e2e8f0"
                                wordBreak="break-all" // Long secret ke liye
                                >
                                {signature ? `${maskSecret(signature)}` : '-'}
                            </Text>
                            <Button
                                variant="outline"
                                size="sm"
                                leftIcon={isCopied ? <CheckCircleIcon /> : <CopyIcon />}
                                onClick={handleCopySignature}
                                isDisabled={!signature || isLoadingRotate}
                                bg={isCopied ? 'green.500' : 'white'}
                                color={isCopied ? 'white' : 'inherit'}
                                _hover={!isCopied ? { bg: 'gray.50' } : {}}
                                minWidth="90px"
                                >
                                    {isCopied ? 'Copied' : 'Copy'}
                            </Button>
                            <Button
                                variant="outline"
                                size="sm"
                                leftIcon={<RepeatIcon />}
                                onClick={handleRotateSignature}
                                // TODO: Redux rotating state use karo
                                isLoading={isLoadingRotate}
                                isDisabled={isLoadingRotate}
                                minWidth="90px"
                            >
                                Rotate
                            </Button>
                        </Box>
                    </>
                )}

                {/* Linked Transfer Webhook Behavior - always visible */}
                <Box sx={{ borderTop: '1px solid #e2e8f0' }}>
                    <Box className='gray-header'>
                        <Text className='black_label'>Linked Transfer Webhook Behavior</Text>
                    </Box>
                    <Box sx={{ padding: '16px 20px', display: 'flex', flexDirection: 'column', gap: '14px' }}>
                        <Checkbox
                            isChecked={sendLinkedStandalone}
                            isDisabled={!isEnabled}
                            onChange={(e) => {
                                setSendLinkedStandalone(e.target.checked);
                                checkIfChanged(webhookUrl, signature, isEnabled, e.target.checked, sendLinkedEcommerce, linkedDelayMinutes);
                            }}
                            colorScheme="facebook"
                        >
                            <Text fontSize="sm">Send webhooks for linked transfers to the endpoint above</Text>
                        </Checkbox>
                        <Checkbox
                            isChecked={sendLinkedEcommerce}
                            onChange={(e) => {
                                setSendLinkedEcommerce(e.target.checked);
                                checkIfChanged(webhookUrl, signature, isEnabled, sendLinkedStandalone, e.target.checked, linkedDelayMinutes);
                            }}
                            colorScheme="facebook"
                        >
                            <Text fontSize="sm">Send webhooks for linked transfers to my e-commerce platform</Text>
                        </Checkbox>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: '12px', marginTop: '4px' }}>
                            <Text fontSize="sm" sx={{ whiteSpace: 'nowrap' }}>Minutes of delay before sending webhook:</Text>
                            <Input
                                type="text"
                                inputMode="numeric"
                                value={delayDisplayValue}
                                onChange={(e) => {
                                    const digitsOnly = e.target.value.replace(/\D/g, '');
                                    if (digitsOnly === '') {
                                        setDelayDisplayValue('');
                                        setLinkedDelayMinutes(0);
                                        checkIfChanged(webhookUrl, signature, isEnabled, sendLinkedStandalone, sendLinkedEcommerce, 0);
                                        return;
                                    }
                                    const parsed = parseInt(digitsOnly, 10);
                                    setDelayDisplayValue(String(parsed));
                                    setLinkedDelayMinutes(parsed);
                                    checkIfChanged(webhookUrl, signature, isEnabled, sendLinkedStandalone, sendLinkedEcommerce, parsed);
                                }}
                                width="100px"
                                size="sm"
                                placeholder="0"
                            />
                        </Box>
                    </Box>
                </Box>

                {/* Save/Cancel Buttons - always visible */}
                <Box sx={{
                    padding: '20px',
                    borderTop: '1px solid #e2e8f0',
                    display: 'flex',
                    gap: '15px',
                    alignItems: 'center',
                }}>
                    <Button
                        colorScheme="blue"
                        background="#ffffff"
                        color="#0C1E46"
                        border="1px solid #0C1E46"
                        padding="8px 20px"
                        size="sm"
                        onClick={handleCancel}
                        isDisabled={isLoadingSave || isActionDisabled}
                    >
                        {constants.CANCEL_LABEL}
                    </Button>
                    <Button
                        colorScheme="blue"
                        background="#0C1E46"
                        padding="8px 20px"
                        size="sm"
                        onClick={handleSave}
                        // TODO: Redux loading use karo
                        isLoading={isLoadingSave}
                        isDisabled={isLoadingSave || isActionDisabled}
                    >
                        {constants.SAVE_TEXT}
                    </Button>
                    {saveError && (
                        <Text
                            color="red.500"
                            fontSize="sm"
                            fontWeight="500"
                        >
                            {saveError}
                        </Text>
                    )}
                </Box>
            </Box>
        </Box>
        {/* MODAL CODE */}
        <Modal isOpen={isOpen} onClose={handleModalClose} isCentered closeOnOverlayClick={false}>
        <ModalOverlay />
        <ModalContent maxWidth={'534px'}  maxHeight={'90vh'} scrollBehavior={'auto'}>
            <ModalHeader className="store-contact-details-edit-header">
                Test Connection Result
                <button
                    className="transfer-create-modal__close-btn"
                    onClick={handleModalClose}
                >
                    <img src="/images/ui/close.svg" />
                </button>
            </ModalHeader>
            <ModalBody>
                {isTestConnectionLoading ? (
                    <Box textAlign="center" py={4}>
                        <Spinner size="lg" color="blue.500" mb={3} />
                        <Text>Testing connection...</Text>
                    </Box>
                ) : getTestingResult ? (
                    <Box 
                        textAlign="center"
                        py={6}
                        px={4}
                    >
                    {getTestingResult.success ? (
                        <CheckCircleIcon 
                            color="green.500" 
                            boxSize={12} 
                            mb={3}
                        />
                        ) : (
                    <WarningIcon 
                        color="red.500" 
                        boxSize={12} 
                        mb={3}
                    />
                    )}
                    {/* Status Text - Success/Failed */}
                    <Text 
                        fontWeight="bold" 
                        fontSize="20px"
                        color={getTestingResult.success ? 'green.700' : 'red.700'}
                        mb={2}
                    >
                        {getTestingResult.success ? 'Success' : 'Failed'}
                    </Text>
                    <Text 
                        color="gray.700"
                        fontSize="16px"
                    >
                        {getTestingResult.message || 'Test completed'}
                    </Text>
                    </Box>
                ) : (
                    <Text textAlign="center" py={4}>No result available</Text>
                )}
            </ModalBody>
            <ModalFooter>
                <Button 
                    colorScheme="blue"
                    onClick={handleModalClose}
                    background="#ffffff"
                    color="#0C1E46"
                    border="1px solid #0C1E46"
                    padding="8px 20px"
                    fontSize={'16px'}
                    height={'35px'}
                    width={'100px'}
                >
                    Close
                </Button>
            </ModalFooter>
        </ModalContent>
        </Modal>
        </>
    );
}